import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import SafeHtml from "@/components/base/SafeHtml";
import { usePageContent } from "@/hooks/usePageContent";

/** Strip HTML tags and return plain text */
function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export default function VisionaryPage() {
  const { getContent } = usePageContent('visionary');

  const heroBadge = getContent('vis-hero-badge') || 'Leadership';
  const heroSub = getContent('vis-hero-sub') || 'The mind behind India\'s first AI-powered land monetization platform.';
  const profileName = stripHtml(getContent('vis-profile-heading')) || 'Mahesh Govindaraju';
  const profileRole = getContent('vis-profile-role') || 'Founder & CEO';
  const profileBody = getContent('vis-profile-body');
  const visionHeading = stripHtml(getContent('vis-vision-heading')) || 'A World Where No Land Goes to Waste';
  const visionBody = getContent('vis-vision-body');
  const philosophyHeading = stripHtml(getContent('vis-philosophy-heading')) || 'Our Philosophy';
  const ctaHeading = stripHtml(getContent('vis-cta-heading')) || 'Be Part of the Vision';

  return (
    <PageLayout>
      <PageHero
        badge={heroBadge}
        title="The"
        highlight="Visionary"
        subtitle={heroSub}
        image="https://readdy.ai/api/search-image?query=Dramatic%20silhouette%20of%20visionary%20entrepreneur%20standing%20on%20hilltop%20overlooking%20modern%20Indian%20city%20skyline%20at%20golden%20hour%2C%20cinematic%20wide%20angle%20composition%2C%20warm%20amber%20sunset%20light%20rays%20piercing%20through%20clouds%2C%20deep%20teal%20sky%20gradient%2C%20inspirational%20leadership%20concept%20photography%2C%20no%20text%2C%20high%20detail%20editorial%20quality&width=1800&height=800&seq=visionary-hero-01&orientation=landscape&nocache=true"
      >
        <div className="flex items-center justify-end gap-6 mt-6">
          <div className="text-center">
            <div className="text-3xl font-display font-bold text-white [text-shadow:0_0_16px_rgba(0,0,0,0.75)]">15+</div>
            <div className="text-sm text-white/80">Years in Real Estate</div>
          </div>
          <div className="w-px h-12 bg-white/25" />
          <div className="text-center">
            <div className="text-3xl font-display font-bold text-white [text-shadow:0_0_16px_rgba(0,0,0,0.75)]">10,000+</div>
            <div className="text-sm text-white/80">Acres Analyzed</div>
          </div>
          <div className="w-px h-12 bg-white/25" />
          <div className="text-center">
            <div className="text-3xl font-display font-bold text-white [text-shadow:0_0_16px_rgba(0,0,0,0.75)]">Govianu</div>
            <div className="text-sm text-white/80">Group</div>
          </div>
        </div>
      </PageHero>

      {/* Visionary Profile */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden w-full max-w-md mx-auto md:mx-0">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_3c6d4b4dcad943b741fd6fa17e3fe528_e25b4e97.jpg"
                alt="Mahesh Govindaraju - Founder & CEO"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="space-y-6">
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider">
                {profileRole}
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950">
                {profileName}
              </h2>
              {profileBody && profileBody.includes('<p>') ? (
                <SafeHtml
                  html={profileBody}
                  className="text-foreground-600 leading-relaxed space-y-4"
                />
              ) : (
                <>
                  <p className="text-foreground-600 leading-relaxed">
                    The founder of CashMyProperty.ai envisioned a platform where every landowner in India —
                    from the NRI managing property remotely to the farmer with ancestral acres — could
                    unlock the true value of their land without selling it. This vision led to the creation
                    of India's first AI-powered land monetization engine.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    With over 15 years of real estate experience through the Govianu Group, the founder
                    understood that land monetization wasn't just about transactions — it was about
                    transforming lives, securing futures, and building generational wealth.
                  </p>
                </>
              )}
              <div className="flex items-center gap-4 pt-2">
                <div className="p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                  <div className="text-2xl font-display font-bold text-foreground-950">15+</div>
                  <div className="text-sm text-foreground-500">Years in Real Estate</div>
                </div>
                <div className="p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                  <div className="text-2xl font-display font-bold text-foreground-950">10,000+</div>
                  <div className="text-sm text-foreground-500">Acres Analyzed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider">
              The Vision
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950">
              {visionHeading}
            </h2>
            {visionBody && visionBody.includes('<p>') ? (
              <SafeHtml
                html={visionBody}
                className="text-foreground-600 leading-relaxed space-y-4"
              />
            ) : (
              <>
                <p className="text-foreground-600 text-lg leading-relaxed">
                  "Every plot of land has a story waiting to be told. Our mission is to decode that story
                  using AI, and connect landowners with partners who can bring that story to life — without
                  ever asking the owner to give up their legacy."
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  The vision extends beyond individual transactions. It's about building an ecosystem where
                  idle land becomes productive, where farmers become partners in development, and where
                  NRIs can sleep peacefully knowing their property is generating income while they are abroad.
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  By 2030, CashMyProperty.ai aims to analyze 100,000 acres of land across India and unlock
                  ₹5,000 Cr in value for landowners — all while keeping ownership intact.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950">
              {philosophyHeading}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="p-8 rounded-2xl card-dark text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5">
                <i className="ri-landscape-line text-2xl text-primary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">
                Land First
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                The landowner's interest always comes first. Every recommendation, every deal, every
                partnership is structured to protect and maximize the owner's value.
              </p>
            </div>
            <div className="p-8 rounded-2xl card-dark text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5">
                <i className="ri-brain-line text-2xl text-primary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">
                Data Driven
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                No guesswork. No intuition. Every decision is backed by millions of data points,
                real-time market analysis, and predictive modeling.
              </p>
            </div>
            <div className="p-8 rounded-2xl card-dark text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5">
                <i className="ri-hand-heart-line text-2xl text-primary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">
                People Focused
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Technology is a tool. People are the purpose. Every landowner is treated with respect,
                transparency, and personalized attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            Join thousands of landowners who are already transforming their idle land into wealth.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Start Your Journey
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}